import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
    Card,
    CardFooter,
    CardCoverPhoto,
    CardBody,
} from '@react/react-spectrum/Card';
import Button from '@react/react-spectrum/Button';

import Tooltip from '@react/react-spectrum/Tooltip';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';

import { interpretKey } from '../../utils/eventUtil';
import { getIcon } from '../../utils/iconUtil';
import '../scss/AppCard.scss';

const PLATFORM_TYPES = {
    MOBILE: 'mobile',
    DESKTOP: 'desktop',
    WEB: 'web',
    TABLET: 'tablet',
};

const BANNER_PLACEMENT = {
    TOP: 'top',
    RIGHT: 'right',
    HIDE: 'hide',
};

export default class AppCard extends Component {
    static propTypes = {
        /**
         * App icon to render in card
         */
        imageSrc: PropTypes.string.isRequired,

        /**
         * A banner image to render in card
         */
        bannerSrc: PropTypes.string.isRequired,

        /**
         * Tells if the banner image is to be shown
         */
        bannerPlacement: PropTypes.oneOf(
            [
                BANNER_PLACEMENT.TOP,
                BANNER_PLACEMENT.RIGHT,
                BANNER_PLACEMENT.HIDE,
            ]
        ),

        /**
         * Card Title
         */
        title: PropTypes.string.isRequired,

        /**
         * Card Flyer Text
         */
        flyerText: PropTypes.string,

        /**
         * Platforms on which app is supported
         */
        supportedPlatform: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOf(
                [
                    PLATFORM_TYPES.DESKTOP,
                    PLATFORM_TYPES.MOBILE,
                    PLATFORM_TYPES.WEB,
                    PLATFORM_TYPES.TABLET,
                ]
            ).isRequired,
            text: PropTypes.string.isRequired,
        })),

        /**
         * Tells if the supported platfrom icons are to be shown
         */
        showSupportedPlatforms: PropTypes.bool,

        /**
         * App subtitle
         */
        subtitle: PropTypes.arrayOf(PropTypes.shape({
            description: PropTypes.string.isRequired,
            showIcon: PropTypes.bool,
            iconType: PropTypes.string,
        })),

        /**
         * Action which can be performed
         */
        actionList: PropTypes.arrayOf(PropTypes.shape({
            actionId: PropTypes.string.isRequired,
            actionText: PropTypes.string.isRequired,
            ariaLabel: PropTypes.string,
            isQuiet: PropTypes.bool,
            isDisabled: PropTypes.bool,
            variant: PropTypes.string.isRequired,
            icon: PropTypes.object,
        })),

        /**
         * Function that gets called on click of an action button
         */
        onAction: PropTypes.func,

        /**
         * Function that gets called on click of card
         */
        onCardSelect: PropTypes.func,
    }

    static defaultProps = {
        flyerText: '',
        bannerPlacement: BANNER_PLACEMENT.TOP,
        showSupportedPlatforms: true,
        supportedPlatform: [],
        actionList: [],
        subtitle: [],
        onCardSelect: () => {},
        onAction: () => {},
    }

    getSupportedIcons() {
        const { supportedPlatform } = this.props;
        const iconList = [];

        supportedPlatform.forEach((platform) => {
            iconList.push(this.renderPlatformIcon(platform.id, platform.text));
        });

        return <div className="appCard-supportedPlatform">{ iconList }</div>;
    }

    getActionButtons() {
        const { actionList, onAction } = this.props;
        const actionButtonList = [];
        actionList.forEach((action) => {
            actionButtonList.push(
                <Button
                    key={action.actionId}
                    onClick={(e) => {
                        e.stopPropagation();
                        onAction(e, action.actionId);
                    }}
                    onKeyDown={interpretKey({
                        onEnter: [e => onAction(e, action.actionId), true, false],
                    })}
                    aria-label={action.ariaLabel || action.actionText}
                    icon={action.icon}
                    quiet={action.isQuiet}
                    disabled={action.isDisabled}
                    variant={action.variant}
                    label={action.actionText} />
            );
        });
        return <div className="appCard-actionButtons">{ actionButtonList }</div>;
    }

    isBannerPlacementRight() {
        const { bannerPlacement } = this.props;
        return bannerPlacement === BANNER_PLACEMENT.RIGHT;
    }

    renderPlatformIcon(iconType, tooltipId) {
        const Icon = getIcon(iconType);
        return (
            <OverlayTrigger key={iconType} placement="top" trigger={['hover', 'focus']} delayShow={1000}>
                <span>
                    {
                        Icon
                        && (<Icon size="S" key={iconType} />)
                    }
                </span>
                <Tooltip>
                    {tooltipId}
                </Tooltip>
            </OverlayTrigger>
        );
    }

    renderSubtitle() {
        const { subtitle } = this.props;
        if (subtitle.length === 1) {
            return subtitle[0].description;
        }
        if (subtitle.length > 1) {
            return (
                <div className={classNames('subTitleList', subtitle[0].showIcon ? '' : 'bulletList')}>
                    {subtitle.map((item) => {
                        const { description, showIcon, iconType } = item;
                        const Icon = (showIcon && iconType)
                            ? getIcon(iconType.toLowerCase())
                            : null;
                        return (
                            <div className={classNames('subTitle-listItem', Icon ? '' : 'bulletList')}>
                                {Icon
                                    && (
                                        <div className="subTitle-icon">
                                            <Icon size={showIcon ? 'S' : 'XXS'} />
                                        </div>
                                    )
                                }
                                {/* eslint-disable react/no-danger */}
                                <div className="subTitle-description" dangerouslySetInnerHTML={{ __html: description }} />
                            </div>
                        );
                    })}
                </div>
            );
        }
        return '';
    }

    renderBannerImage() {
        const { bannerPlacement, bannerSrc, title } = this.props;
        if (bannerPlacement === BANNER_PLACEMENT.RIGHT) {
            return (
                <div className="appCard-rightSection">
                    <img src={bannerSrc} alt={title} />
                </div>
            );
        }
        return <CardCoverPhoto src={bannerSrc} />;
    }

    renderFlyer() {
        const { flyerText } = this.props;
        if (!flyerText) {
            return null;
        }
        return (
            <div className="appCard-Flyer">
                {flyerText}
            </div>
        );
    }

    render() {
        const {
            imageSrc,
            bannerPlacement,
            bannerSrc,
            title,
            flyerText,
            supportedPlatform,
            showSupportedPlatforms,
            subtitle,
            actionList,
            onCardSelect,
            className,
            ...otherProps
        } = this.props;

        const bannerPlacementRightClass = this.isBannerPlacementRight() ? 'bannerPlacementRight' : '';
        const onKeyDownCb = interpretKey({ onEnter: [onCardSelect, true, true] });
        return (
            <Card
                onClick={onCardSelect}
                className={classNames('appCard', className, bannerPlacementRightClass)}
                allowsSelection={false}
                onKeyDown={onKeyDownCb}
                {...otherProps}>
                {this.renderFlyer()}
                {bannerPlacement !== BANNER_PLACEMENT.HIDE && this.renderBannerImage()}
                <div className={classNames('appCard-leftSection', bannerPlacementRightClass)}>
                    <div className="appCard-header">
                        <div className="appCard-name-icon">
                            {imageSrc && <img className="appCard-icon" src={imageSrc} alt={title} />}
                            <div className="appCard-name">{title}</div>
                        </div>
                        {showSupportedPlatforms && this.getSupportedIcons()}
                    </div>
                    <CardBody>
                        {this.renderSubtitle()}
                    </CardBody>
                    {actionList.length > 0 && (
                        <CardFooter>
                            <div className="appCard-footer">
                                {this.getActionButtons()}
                            </div>
                        </CardFooter>
                    )}
                </div>
            </Card>
        );
    }
}
